// Zod DTOs for the public `/v1/plans` pricing-config surface (P5-14, phase-5
// "API integration: Public pricing"). A plan tier is STATIC presentation
// config (name/price/features/CTA for the marketing pricing screen) — NOT
// commerce/billing state (that's a separate Phase-4 entitlement surface) —
// so this file only describes the read-only shape served by `GET /v1/plans`,
// no create/update request DTOs.
//
// Shape is FROZEN byte-identical to the FE-local `PricingPlan` interface
// already shipped in `apps/web/src/data/plans.ts` (P1-18) so the frontend
// cutover ticket (P5-15) needs no UI rework — see that file's own doc
// comment for the per-field rationale this mirrors.
import { z } from 'zod';

export const planTierSchema = z.object({
  /** Stable slug — the plan's identity across cache + future billing calls. */
  code: z.string().min(1),
  name: z.string().min(1),
  /** Pre-formatted price label, e.g. `$9` or `Custom`. */
  priceDisplay: z.string().min(1),
  /** Amount in the currency's minor unit (cents); `null` for quote-only tiers. */
  priceMinor: z.number().int().nonnegative().nullable(),
  /** ISO 4217 currency code. */
  currency: z.string().length(3),
  /** Billing cadence label, e.g. `per month`, `forever`, `per campus`. */
  period: z.string().min(1),
  description: z.string(),
  features: z.array(z.string().min(1)),
  /** The recommended tier — rendered elevated with the POPULAR flag. */
  popular: z.boolean(),
  /** Call-to-action label. */
  cta: z.string().min(1),
});
export type PlanTier = z.infer<typeof planTierSchema>;

// ─── GET /v1/plans response ──────────────────────────────────────────────

export const plansListResponseSchema = z.object({ items: z.array(planTierSchema) });
export type PlansListResponse = z.infer<typeof plansListResponseSchema>;
